import NetworkAssessment from '@/helpers/networking/NetworkAssessment';
import { applyExternalEvalSubjectNameInQuestionText } from '@/helpers/externalEvalQuestionText';
import {
  isForceInputRequired,
  isAnswerMissingForQuestionnaire,
} from '@/helpers/questionnaireValidation';
import { normalizeRangeSliderAnswerForStorage } from '@/helpers/assessmentDimensionScore';

export default {
  state: {
    assessment: null,
    selectedQuestion: null,
    selectedQuestionType: null,
    questions: [],
    progress: null,
    forceInput: false,
    multipleChoose: 0,
    questionnaireIndex: 0,
    involvedUsers: [],
    progressText: null,
    chapterText: null,
    assesmentType: null,
    answer: null,
    locale: 'en',
    icQuestions: [],
    selectedIcQuestion: null,
    icQuestionnaireIndex: 0,
    icTextQuestionWeight: null,
    icForceInput: false,
    selectedIcQuestionType: null,
    isDone: false,
    isExpired: false,
    currentIcAnswerEntry: null,
    showNextButton: true,
    userId: '',
  },
  getters: {
    getProgress: (state) => (state.questions?.length
      ? ((state.questionnaireIndex + 1) * 100) / state.questions.length
      : 0),
    getSelectedQuestion: (state) => state.selectedQuestion,
    getSelectedQuestionType: (state) => state.selectedQuestionType,
    getSelectedQuestionProgressText: (state) => state.progressText,
    getSelectedQuestionChapterText: (state) => state.chapterText,
    getQuestionnaireIndex: (state) => state.questionnaireIndex,
    getInvolvedUsers: (state) => state.involvedUsers,
    getSelectedIcQuestionType: (state) => state.selectedIcQuestionType,
    getIcQuestionnaireIndex: (state) => state.icQuestionnaireIndex,
    getForceInput: (state) => state.forceInput,
    getIcForceInput: (state) => state.icForceInput,
    getShowNextButton: (state) => state.showNextButton,
    getUserId: (state) => state.userId,
    getAnsweredQuestions: (state) => {
      if (!state.questions || !state.questions.length) return [];
      const locale = state.locale || 'en';
      const creatorName = state.assessment?.creator?.name ?? '';
      return state.questions.map((q, index) => {
        const rawText = q?.properties?.content?.text_question?.[locale] ?? '';
        const text = applyExternalEvalSubjectNameInQuestionText(rawText, creatorName);
        const data = q?.properties?.data;
        const questionType = q?.type ?? '';
        let answerPreview = null;
        let answerValue = null;
        if (data?.value !== undefined && data?.value !== null && data?.value !== '') {
          if (typeof data.value === 'number') {
            answerValue = data.value;
            answerPreview = String(data.value);
          } else if (Array.isArray(data.value)) {
            answerPreview = data.value.length ? `(${data.value.length})` : null;
          } else {
            answerPreview = String(data.value).slice(0, 40);
          }
        } else if (data?.integrativeComplexityValues?.length) {
          answerPreview = `(${data.integrativeComplexityValues.length})`;
        }
        return {
          index,
          questionText: text,
          answerPreview,
          answerValue,
          questionType,
          fullQuestionText: text,
          hasAnswer: answerPreview != null,
        };
      });
    },
  },
  mutations: {
    setShowNextButton: (state, data) => {
      state.showNextButton = data;
    },
    setAssessment: (state, data) => {
      state.assessment = data;
    },
    setCurrentIcAnswerEntry: (state, data) => {
      state.currentIcAnswerEntry = data;
    },
    setQuestionnaireIndex: (state, data) => {
      const index = typeof data === 'number' && !Number.isNaN(data) ? data : 0;
      state.questionnaireIndex = Math.max(0, Math.min(index, state.questions.length - 1));
    },
    setQuestions: (state, data) => {
      state.questions = data;
    },
    setProgress: (state, data) => {
      state.progress = data;
    },
    setForceInput: (state) => {
      state.forceInput = isForceInputRequired(state.selectedQuestion);
    },
    setMultipleChoose: (state) => {
      state.multipleChoose = state.selectedQuestion.properties.options.multipleChoose;
    },
    selectInvolvedUser: (state, index) => {
      const selectedUsers = state.involvedUsers.filter((el) => el.isChecked)
        .map((el) => el._id);

      if (selectedUsers.length < state.multipleChoose) {
        state.involvedUsers[index].isChecked = !state.involvedUsers[index].isChecked;
      } else if (state.involvedUsers[index].isChecked) {
        state.involvedUsers[index].isChecked = false;
      }

      state.answer = state.involvedUsers.filter((el) => el.isChecked)
        .map((el) => el._id);
    },
    setInvolvedUsers: (state) => {
      if (state.selectedQuestionType !== 'KssChampionUserListQuestion') {
        state.involvedUsers = [];
        return;
      }

      state.selectedQuestion.properties.data.involvedUsers.forEach(
        (el) => {
          let isChecked = false;
          if (Array.isArray(state.answer)) {
            isChecked = state.answer.includes(el._id);
          }
          state.involvedUsers.push({
            ...el,
            isChecked,
          });
        },
      );
    },
    setSelectedQuestion: (state) => {
      state.selectedQuestion = state.questions[state.questionnaireIndex];
    },
    setSelectedQuestionType: (state) => {
      switch (state.selectedQuestion.type) {
        case 'integrativeComplexity':
          state.selectedQuestionType = 'IntegrativeComplexityQuestion';
          break;
        case 'rangeSlider':
          state.selectedQuestionType = 'SliderQuestion';
          break;
        case 'chooseFromList':
          state.selectedQuestionType = 'KssChampionUserListQuestion';
          break;
        case 'textField':
          state.selectedQuestionType = 'TextQuestion';
          break;
        default:
          state.selectedQuestionType = null;
          break;
      }
    },
    setSelectedQuestionProgressText: (state) => {
      state.progressText = state.selectedQuestion
        .properties.content.text_questionProgress?.[state.locale];
    },
    setSelectedQuestionChapterText: (state) => {
      state.chapterText = state.selectedQuestion
        .properties.content.text_chapter?.[state.locale];
    },
    setAnswer: (state, value) => {
      state.answer = value;
    },
    initAnswer: (state) => {
      if (state.selectedQuestion.properties.data !== undefined) {
        state.answer = state.selectedQuestion.properties.data.value;
      } else {
        state.answer = null;
      }
    },
    setLocale: (state, locale) => {
      state.locale = locale;
    },
    setIcTextQuestionWeight: (state) => {
      state.icTextQuestionWeight = state.selectedQuestion.properties.content.text_question_weight;
    },
    setSelectedIcQuestion: (state) => {
      state.selectedIcQuestion = state.icQuestions[state.icQuestionnaireIndex];
    },
    setSelectedIcQuestionType: (state) => {
      state.selectedIcQuestionType = state.selectedIcQuestion.type;
    },
    setIcQuestionnaireIndex: (state, data) => {
      state.icQuestionnaireIndex = data;
    },
    setIcAnswer: (state, value) => {
      state.selectedIcQuestion.data.value = value;
    },
    setInitIcQuestions: (state) => {
      state.icQuestions = [{
        properties: {
          dimensionName: 'integrativeComplexity',
          dimension: 'x',
          content: {
            text_chapter: {
              en: state.selectedQuestion.properties.content.text_chapter.en,
              de: state.selectedQuestion.properties.content.text_chapter.de,
            },
            text_questionProgress: {
              en: 'Question 1 of 2',
              de: 'Frage 1 von 2',
            },
            text_question: {
              en: state.selectedQuestion.properties.content.text_question_page1.en,
              de: state.selectedQuestion.properties.content.text_question_page1.de,
            },
          },
          data: {
            integrativeComplexityValues: state.selectedQuestion
              .properties.data.integrativeComplexityValues.filter((el) => el.positive),
          },
        },
        positive: true,
        class: 'question_element',
        type: 'AddIntegrativeComplexityValues',
      }, {
        properties: {
          dimensionName: 'integrativeComplexity',
          dimension: 'x',
          content: {
            text_chapter: {
              en: '', // TODO
              de: '', // TODO
            },
            text_questionProgress: {
              en: 'Question 2 of 2',
              de: 'Frage 2 von 2',
            },
            text_question: {
              en: state.selectedQuestion.properties.content.text_question_page2.en,
              de: state.selectedQuestion.properties.content.text_question_page2.de,
            },
          },
          data: {
            integrativeComplexityValues: state.selectedQuestion
              .properties.data.integrativeComplexityValues.filter((el) => !el.positive),
          },
        },
        positive: false,
        class: 'question_element',
        type: 'AddIntegrativeComplexityValues',
      }];
    },
    addRateIcQuestion: (state, value) => {
      state.icQuestions.push({
        properties: {
          dimensionName: 'integrativeComplexity',
          dimension: 'x',
          content: {
            text_chapter: {
              en: '',
              de: '',
            },
            text_questionProgress: {
              en: 'Question 2 of 2',
              de: 'Frage 2 von 2',
            },
            text_question: {
              en: state.icTextQuestionWeight.en,
              de: state.icTextQuestionWeight.de,
            },
          },
        },
        data: value,
        class: 'question_element',
        type: 'RateIntegrativeComplexityValue',
      });
    },
    setIcForceInput: (state, data) => {
      state.icForceInput = data;
    },
    setIsDone: (state, value) => {
      state.isDone = value;
    },
    setIsExpired: (state, value) => {
      state.isExpired = value;
    },
    removeQuestionType: (state) => {
      state.selectedQuestionType = null;
    },
    removeInvolvedUsers: (state) => {
      state.involvedUsers = [];
    },
    removeSelectedIcQuestionType: (state) => {
      state.selectedIcQuestionType = null;
    },
    setUserId: (state, data) => {
      state.userId = data;
    },
  },
  actions: {
    async getAssessment({
      commit,
      state,
    }, {
      id,
      locale,
    }) {
      const { data } = await NetworkAssessment.getAssessment(id);
      commit('setLocale', locale);
      commit('setAssessment', data.assessment);
      commit('setProgress', data.assessment.progress);
      commit('setQuestions', data.assessment.assessmentQuestionArray);
      commit('setQuestionnaireIndex', data.assessment.saved_until_question);
      commit('setSelectedQuestion');
      commit('setForceInput');
      commit('setMultipleChoose');
      commit('initAnswer');
      commit('setSelectedQuestionType');
      commit('setUserId', data.assessment.assigned_to);
      if (state.selectedQuestionType === 'IntegrativeComplexityQuestion') {
        commit('setIcTextQuestionWeight');
        commit('setInitIcQuestions');
        commit('setSelectedIcQuestion');
        commit('setSelectedIcQuestionType');
      } else {
        commit('removeSelectedIcQuestionType');
      }
      commit('setInvolvedUsers');
      commit('setSelectedQuestionProgressText');
      commit('setSelectedQuestionChapterText');
    },
    async setNextQuestion({
      state,
      commit,
      getters,
    }) {
      if (['AddIntegrativeComplexityValues', 'RateIntegrativeComplexityValue'].includes(state.selectedIcQuestionType)) {
        if (state.currentIcAnswerEntry !== null && state.currentIcAnswerEntry !== '') {
          const answer = {
            class: 'integrativeComplexityValue',
            positive: state.selectedIcQuestion.positive,
            text: state.currentIcAnswerEntry,
          };
          commit('addRateIcQuestion', answer);
        }
        commit('setCurrentIcAnswerEntry', null);

        const previousIndex = state.icQuestionnaireIndex;
        if (state.icQuestionnaireIndex + 1 < state.icQuestions.length) {
          state.icQuestionnaireIndex += 1;
        }
        commit('setIcQuestionnaireIndex', state.icQuestionnaireIndex);
        commit('setSelectedIcQuestion');
        commit('setSelectedIcQuestionType');

        if (state.icQuestionnaireIndex === previousIndex) {
          state.selectedQuestion.properties.data.integrativeComplexityValues = state.icQuestions
            .map((el) => el.data)
            .filter((e) => e);
        } else {
          return;
        }
      } else {
        // Required only when CMS sets forceInput === true (strict; sticky does not imply required)
        if (isForceInputRequired(state.selectedQuestion)) {
          if (isAnswerMissingForQuestionnaire(state.answer)) {
            return;
          }
        }

        // Set answer to questionnaire
        const normalizedAnswer = normalizeRangeSliderAnswerForStorage(
          state.selectedQuestion.type,
          state.answer,
        );
        if (state.selectedQuestion.properties.data !== undefined) {
          state.selectedQuestion.properties.data.value = normalizedAnswer;
        } else {
          state.selectedQuestion.properties.data = { value: normalizedAnswer };
        }
      }

      if (state.assessment.saved_until_question <= state.questionnaireIndex) {
        state.assessment.saved_until_question = state.questionnaireIndex + 1;
      }

      state.assessment.progress = getters.getProgress;

      if (state.questionnaireIndex + 1 === state.questions.length) {
        // Before submit: only forceInput === true must have a non-missing answer
        const firstUnanswered = state.questions.findIndex((q, i) => {
          if (!isForceInputRequired(q)) return false;
          if (i === state.questionnaireIndex) return false; // current question already validated above
          const val = q?.properties?.data?.value;
          return isAnswerMissingForQuestionnaire(val);
        });

        if (firstUnanswered !== -1) {
          // Jump to the first unanswered required question instead of submitting
          state.questionnaireIndex = firstUnanswered;
          commit('setQuestionnaireIndex', state.questionnaireIndex);
          commit('setSelectedQuestion');
          commit('setSelectedQuestionType');
          commit('setForceInput');
          commit('setMultipleChoose');
          commit('initAnswer');
          commit('setInvolvedUsers');
          commit('setSelectedQuestionProgressText');
          commit('setSelectedQuestionChapterText');
          throw new Error('Please answer all required questions before submitting.');
        }

        // CRITICAL: Save the last answer before submitting
        const { involvedUsers, ...answerData } = state.selectedQuestion.properties.data || {};
        const progress = getters.getProgress;

        await NetworkAssessment.updateAnswer(
          state.assessment._id,
          state.questionnaireIndex,
          answerData,
          progress,
        );

        await NetworkAssessment.submitAssessment(state.assessment._id);
        commit('setIsDone', true);
        return;
      }

      // Use incremental update - sends only the changed answer
      // This reduces bandwidth by 90%+ compared to sending the entire assessment object
      // Extract answer data from the selected question, excluding involvedUsers
      // (involvedUsers is input data for the question, not the answer)
      const { involvedUsers: _, ...answerData } = state.selectedQuestion.properties.data || {};

      // Calculate progress
      const progress = getters.getProgress;

      // Use new incremental update endpoint
      const response = await NetworkAssessment.updateAnswer(
        state.assessment._id,
        state.questionnaireIndex,
        answerData,
        progress,
      );

      // Update local assessment state with response (minimal data only)
      if (response?.data) {
        // Sync the response back to local state to ensure we have the latest server state
        Object.assign(state.assessment, {
          progress: response.data.progress,
          status: response.data.status,
          date_lastUpdated: response.data.date_lastUpdated,
          ...(response.data.date_firstUpdate && {
            date_firstUpdate: response.data.date_firstUpdate,
          }),
        });
      }

      if (state.questionnaireIndex + 1 < state.questions.length) {
        state.questionnaireIndex += 1;
      }
      commit('setQuestionnaireIndex', state.questionnaireIndex);
      commit('setSelectedQuestion');
      commit('setSelectedQuestionType');
      commit('setForceInput');
      commit('setMultipleChoose');
      if (state.selectedQuestionType === 'IntegrativeComplexityQuestion') {
        commit('setIcQuestionnaireIndex', 0);
        commit('setIcTextQuestionWeight');
        commit('setInitIcQuestions');
        commit('setSelectedIcQuestion');
        commit('setSelectedIcQuestionType');
      } else {
        commit('removeSelectedIcQuestionType');
      }
      commit('initAnswer');
      commit('setInvolvedUsers');
      commit('setSelectedQuestionProgressText');
      commit('setSelectedQuestionChapterText');
    },
    setPreviousQuestion({
      state,
      commit,
    }) {
      if (['AddIntegrativeComplexityValues', 'RateIntegrativeComplexityValue'].includes(state.selectedIcQuestionType)) {
        const previousIndex = state.icQuestionnaireIndex;
        if (state.icQuestionnaireIndex >= 1) {
          state.icQuestionnaireIndex -= 1;
        }
        commit('setIcQuestionnaireIndex', state.icQuestionnaireIndex);
        commit('setSelectedIcQuestion');
        commit('setSelectedIcQuestionType');

        // State index 0 to previous
        if (previousIndex !== state.icQuestionnaireIndex) {
          return;
        }
      }

      if (state.questionnaireIndex >= 1) {
        state.questionnaireIndex -= 1;
      }

      commit('setQuestionnaireIndex', state.questionnaireIndex);
      commit('setSelectedQuestion');
      commit('setSelectedQuestionType');
      commit('setForceInput');
      commit('setMultipleChoose');
      if (state.selectedQuestionType === 'IntegrativeComplexityQuestion') {
        commit('setIcTextQuestionWeight');
        commit('setInitIcQuestions');
        commit('setIcQuestionnaireIndex', state.icQuestions.length - 1);
        commit('setSelectedIcQuestion');
        commit('setSelectedIcQuestionType');
      } else {
        commit('removeSelectedIcQuestionType');
      }
      commit('initAnswer');
      commit('setInvolvedUsers');
      commit('setSelectedQuestionProgressText');
      commit('setSelectedQuestionChapterText');
    },
    jumpToQuestion({
      state,
      commit,
    }, index) {
      if (index < 0 || index >= state.questions.length) return;

      const maxReachable = state.assessment?.saved_until_question ?? state.questionnaireIndex;
      if (index > maxReachable) return;

      // Auto-save: persist the current answer if it changed before jumping away
      if (state.selectedQuestion && state.selectedQuestionType !== 'IntegrativeComplexityQuestion') {
        const storedValue = state.selectedQuestion.properties.data?.value;
        const currentValue = normalizeRangeSliderAnswerForStorage(
          state.selectedQuestion.type,
          state.answer,
        );
        const hasChanged = Array.isArray(currentValue) || Array.isArray(storedValue)
          ? JSON.stringify(currentValue) !== JSON.stringify(storedValue)
          : currentValue !== storedValue;

        const shouldPersistRangeSliderSkip = state.selectedQuestion.type === 'rangeSlider'
          && Number.isNaN(currentValue)
          && (storedValue === null || storedValue === undefined);

        if (hasChanged || shouldPersistRangeSliderSkip) {
          if (state.selectedQuestion.properties.data !== undefined) {
            state.selectedQuestion.properties.data.value = currentValue;
          } else {
            state.selectedQuestion.properties.data = { value: currentValue };
          }
          const { involvedUsers: _, ...answerData } = state.selectedQuestion.properties.data || {};
          NetworkAssessment.updateAnswer(
            state.assessment._id,
            state.questionnaireIndex,
            answerData,
            state.assessment.progress,
          ).catch((err) => console.warn('Auto-save on jump failed:', err));
        }
      }

      state.questionnaireIndex = index;
      commit('setQuestionnaireIndex', state.questionnaireIndex);
      commit('setSelectedQuestion');
      commit('setSelectedQuestionType');
      commit('setForceInput');
      commit('setMultipleChoose');
      if (state.selectedQuestionType === 'IntegrativeComplexityQuestion') {
        commit('setIcQuestionnaireIndex', 0);
        commit('setIcTextQuestionWeight');
        commit('setInitIcQuestions');
        commit('setSelectedIcQuestion');
        commit('setSelectedIcQuestionType');
      } else {
        commit('removeSelectedIcQuestionType');
      }
      commit('initAnswer');
      commit('setInvolvedUsers');
      commit('setSelectedQuestionProgressText');
      commit('setSelectedQuestionChapterText');
    },
  },
};
